/* eslint-disable no-empty */
/**
 * Phase A R3 — useLibrary hook.
 *
 * Local music folder picker for the DJ console. Uses File System Access
 * API (`window.showDirectoryPicker`) — Chromium-only today, will also
 * work inside the future Electron wrapper.
 *
 * Persists the picked `FileSystemDirectoryHandle` in IndexedDB under
 * key `dj-library-handle` so the user only needs to pick a folder once.
 * On reload we re-prompt for read permission (browser requirement) but
 * the handle survives.
 *
 * Returns:
 *   - supported       : bool — true if FSA API is available
 *   - hasHandle       : bool — handle is loaded (from IDB or freshly picked)
 *   - folderName      : string — picked folder name (display only)
 *   - tracks          : array<{ id, name, path, file: File, size, key?, bpm?, duration? }>
 *   - status          : 'idle' | 'picking' | 'reading' | 'ready' | 'denied' | 'error' | 'unsupported'
 *   - pickFolder()    : prompt for folder + scan
 *   - refresh()       : re-scan the saved folder
 *   - clear()         : forget the saved folder
 *   - reattachFromIdb : re-attach saved folder + request permission
 */
import { useCallback, useEffect, useState } from "react";

const IDB_NAME = "stackroom-dj-library";
const IDB_STORE = "handles";
const IDB_KEY = "dj-library-handle";
// Phase A R6 B10 — `.mp4` audio-only containers are common in DJ pools.
// Web Audio's decodeAudioData auto-detects codec, so the only thing
// blocking these was the extension whitelist below.
const AUDIO_EXTS = ["mp3", "m4a", "wav", "flac", "ogg", "aac", "aif", "aiff", "mp4"];

const supported = typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";

function idbOpen() {
  return new Promise((res, rej) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      try { req.result.createObjectStore(IDB_STORE); } catch {}
    };
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}
async function idbGet(key) {
  try {
    const db = await idbOpen();
    return new Promise((res) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => res(req.result || null);
      req.onerror = () => res(null);
    });
  } catch { return null; }
}
async function idbSet(key, value) {
  try {
    const db = await idbOpen();
    return new Promise((res) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => res(true);
      tx.onerror = () => res(false);
    });
  } catch { return false; }
}
async function idbDel(key) {
  try {
    const db = await idbOpen();
    return new Promise((res) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = () => res(true);
      tx.onerror = () => res(false);
    });
  } catch { return false; }
}

async function walkDir(handle, prefix = "") {
  const out = [];
  for await (const [name, child] of handle.entries()) {
    if (child.kind === "directory") {
      out.push(...await walkDir(child, `${prefix}${name}/`));
    } else if (child.kind === "file") {
      const ext = name.split(".").pop()?.toLowerCase();
      if (!AUDIO_EXTS.includes(ext)) continue;
      try {
        const file = await child.getFile();
        out.push({
          id: `${prefix}${name}`,
          name,
          path: `${prefix}${name}`,
          file,
          size: file.size,
        });
      } catch {}
    }
  }
  return out;
}

export function useLibrary() {
  const [hasHandle, setHasHandle] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [tracks, setTracks] = useState([]);
  const [status, setStatus] = useState(supported ? "idle" : "unsupported");
  const [error, setError] = useState("");
  const [handleRef] = useState({ current: null });

  // Try to re-attach a saved handle on mount (without prompting yet).
  useEffect(() => {
    if (!supported) return;
    (async () => {
      const saved = await idbGet(IDB_KEY);
      if (saved) {
        handleRef.current = saved;
        setHasHandle(true);
        setFolderName(saved.name || "");
        // We don't auto-prompt for read perm; that needs a user gesture.
        // The UI can call reattachFromIdb() to request it.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scan = useCallback(async (handle) => {
    setStatus("reading");
    setError("");
    try {
      const found = await walkDir(handle);
      setTracks(found);
      setStatus("ready");
    } catch (e) {
      setError(String(e));
      setStatus("error");
    }
  }, []);

  const pickFolder = useCallback(async () => {
    if (!supported) { setStatus("unsupported"); return; }
    try {
      setStatus("picking");
      const handle = await window.showDirectoryPicker({ id: "stackroom-dj-library", mode: "read" });
      handleRef.current = handle;
      setHasHandle(true);
      setFolderName(handle.name || "");
      await idbSet(IDB_KEY, handle);
      await scan(handle);
    } catch (e) {
      // User cancelled or denied — non-fatal.
      setStatus(handleRef.current ? "ready" : "idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan]);

  const reattachFromIdb = useCallback(async () => {
    if (!supported) { setStatus("unsupported"); return false; }
    const handle = handleRef.current || (await idbGet(IDB_KEY));
    if (!handle) { setStatus("idle"); return false; }
    handleRef.current = handle;
    try {
      const perm = await handle.queryPermission({ mode: "read" });
      if (perm !== "granted") {
        const req = await handle.requestPermission({ mode: "read" });
        if (req !== "granted") { setStatus("denied"); return false; }
      }
      setHasHandle(true);
      setFolderName(handle.name || "");
      await scan(handle);
      return true;
    } catch (e) {
      setError(String(e));
      setStatus("error");
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan]);

  const refresh = useCallback(async () => {
    if (!handleRef.current) return;
    await scan(handleRef.current);
  }, [scan, handleRef]);

  const clear = useCallback(async () => {
    handleRef.current = null;
    setHasHandle(false);
    setFolderName("");
    setTracks([]);
    setStatus("idle");
    await idbDel(IDB_KEY);
  }, [handleRef]);

  return {
    supported,
    hasHandle,
    folderName,
    tracks,
    status,
    error,
    pickFolder,
    reattachFromIdb,
    refresh,
    clear,
  };
}
